import React, { PropsWithChildren } from 'react';

import { cx } from '@pt-group-fe/styled-system/css';
import { cmsLinkServer } from '@pt-group-fe/styled-system/recipes';
import Link from 'next/link';

import type { Cms_SitemapTreeItem, Cms_SocialLink, Cms_Target } from '@lib/services/cms';
import { PageProps } from '@lib/services/page';
import { routes } from '@lib/services/routes';

import { Button } from '../button';
import type { ButtonProps } from '../button';
import { IconType } from '../icon';

import { getNavItemLinkUrl } from './cmsLink.utils';

interface CmsNavLinkProps extends PropsWithChildren {
  navItem: Cms_SitemapTreeItem;
  className?: string;
  button?: ButtonProps;
  pageProps: PageProps;
}

interface ContactSocialLinkProps extends PropsWithChildren {
  link: Cms_SocialLink;
  className?: string;
  target?: Cms_Target;
  button?: ButtonProps;
}

const CmsNavLinkServer = React.forwardRef<HTMLAnchorElement, CmsNavLinkProps>((props, ref) => {
  const { navItem, className, children, button, pageProps } = props;
  const rootPath = routes.getLocalizedRootPath(pageProps);

  const { navLink } = cmsLinkServer();

  const content = navItem.title || children || (
    <Button icon={button?.icon || 'arrow-right'} variant={button?.variant} size={button?.size}>
      {button?.children || navItem.title}
    </Button>
  );
  const url = getNavItemLinkUrl(navItem) || rootPath;

  if (navItem.type === 'internal') {
    return (
      <Link
        ref={ref}
        className={cx(navLink, className)}
        href={url}
        scroll={true}
        target={navItem.target || '_self'}
      >
        {content}
      </Link>
    );
  }

  if (url) {
    return (
      <a
        ref={ref}
        className={cx(navLink, className)}
        rel="nofollow"
        href={url}
        target={navItem.target || '_blank'}
      >
        {content}
      </a>
    );
  }

  return null;
});
CmsNavLinkServer.displayName = 'CmsNavLinkServer';

const CmsSocialLinkServer = React.forwardRef<HTMLAnchorElement, ContactSocialLinkProps>(
  (props, ref) => {
    const { link, className } = props;

    const { socialLink } = cmsLinkServer();

    let icon: IconType | undefined = undefined;
    switch (link.plaform) {
      case 'LINKEDIN':
        icon = 'linkedin';
        break;
      default:
        return undefined;
    }

    let text: string | undefined = undefined;
    switch (link.plaform) {
      case 'LINKEDIN':
        text = 'Linkedin';
        break;
      default:
        return undefined;
    }

    return (
      <a
        ref={ref}
        href={link.link}
        target="_blank"
        rel="nofollow noreferrer"
        className={cx(socialLink, className)}
      >
        <Button
          variant="secondary"
          size="small"
          icon={icon}
          srOnly={`Open ${text || link.plaform} profile`}
        >
          {text}
        </Button>
      </a>
    );
  }
);
CmsSocialLinkServer.displayName = 'CmsSocialLinkServer';

export { CmsNavLinkServer, CmsSocialLinkServer };
